import React, { useState, useEffect } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { createStructuredSelector } from "reselect";
import { Button, Tooltip, Typography, Modal, Form, Space } from "antd";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Statuses, Keys, TotalSteps } from "../constant/status";
import ACTIONS from "../store/action";
import { selectSite, selectIsFetching, selectIsLoadingEnabled, selectPatientCycles } from "../store/selector";
import { ApiNames } from "../api/api";
import { OrderDateFormItem } from "../component/form/OrderDateFormItem";
import { EstimatedDateFormItem } from "./form/EstimatedDateFormItem";
import IsOverdueIcon from "../assets/svg/IsOverdueIcon";
import { getNewCycle, isOverdue } from "../utils/cycles";
import styled from "styled-components";

const { Text, Title } = Typography;

const StyledDiv = styled.div`
  .ant-row {
    display: block;
  }
`;

const ButtonContainer = styled(Space)`
  display: flex;
  justify-content: ${(props) => (props.isCalenderView ? "center" : "end")};
  align-items: center;

  @media (max-width: 1600px) {
    transform: ${(props) => (props.isCalenderView ? "scale(0.90)" : "scale(1)")};
  }

  @media (max-width: 1410px) {
    transform: ${(props) => (props.isCalenderView ? "scale(0.8)" : "scale(1)")};
  }
`;

const ActionButtonComponent = ({
  patient,
  order,
  showOverdue = true,
  editCurrentOrderAct,
  setNextCycleAct,
  isSearchWithoutRefreshLoading,
  setPharmacyPatientAct,
  addPatientCycleAct,
  actionButtonId,
  site,
  isCalenderView = false
}) => {
  const dispatch = useDispatch();
  const patientCycles = useSelector(selectPatientCycles);
  const [isLoading, setIsLoading] = useState(false);
  const [ordedModalVisible, setOrdedModalVisible] = useState(false);
  const [noCyclesModalVisible, setNoCyclesModalVisible] = useState(false);
  const [datesForm] = Form.useForm();
  const isLoadingEnabled = useSelector(selectIsLoadingEnabled);

  const { t } = useTranslation();

  useEffect(() => {
    if (!isLoadingEnabled && !isSearchWithoutRefreshLoading) {
      setIsLoading(false);
      setNoCyclesModalVisible(false);
      setOrdedModalVisible(false);
    }
  }, [isLoadingEnabled, isSearchWithoutRefreshLoading]);

  const displayOrder = order || patient.currentOrder;

  useEffect(() => {
    const { orderDate, estimatedDelivery } = displayOrder;

    datesForm.setFieldsValue({
      orderDate: orderDate ? moment(orderDate) : orderDate,
      estimatedDelivery: estimatedDelivery ? moment(estimatedDelivery) : estimatedDelivery
    });
  }, [displayOrder]);

  const isDone = (statusObj, isFreeDrug) => statusObj.step(isFreeDrug) === TotalSteps;
  const isLastCycle = (statusObj, isFreeDrug, orderIndex) =>
    numberOfCycles <= orderIndex + 1 && isDone(statusObj, isFreeDrug);
  const isCurrentCycle = (patientObj) => patientObj.currentOrder.id === patientObj.pharmacyPatientCurrentOrder;
  const isLastStatusAllOrdesrView = (statusObj, isFreeDrug, patientObj) =>
    isDone(statusObj, isFreeDrug) && !isCurrentCycle(patientObj);
  const setIsLoadingEnabledAct = (isLoadingEnabled) => dispatch(ACTIONS.setIsLoadingEnabled(isLoadingEnabled));

  const onClickSetInactive = () => {
    setIsLoading(true);
    setIsLoadingEnabledAct(true);
    const pharmacyPatient = {
      id: patient.id,
      isActive: false
    };
    setPharmacyPatientAct(pharmacyPatient);
    setNoCyclesModalVisible(false);
  };

  const onClickAddCycle = () => {
    setIsLoading(true);
    setIsLoadingEnabledAct(true);
    const cycles = patientCycles || [];
    const lastCycle =
      cycles.length > 0
        ? cycles.reduce((max, cycle) => (cycle.orderIndex > max.orderIndex ? cycle : max), cycles[0])
        : patient.currentOrder;

    const isCreatingFromCurrentOrder = patient.pharmacyPatientCurrentOrder === lastCycle.id;

    const setNextCycle = isCreatingFromCurrentOrder
      ? {
          pharmacyPatient,
          currentAdministrationDate: administrationDate,
          orderIndex: lastCycle.orderIndex
        }
      : null;

    const newCycle = getNewCycle(patient, lastCycle);
    addPatientCycleAct(newCycle, setNextCycle);
  };

  const onClickDrugOrdered = (statusObj, isFreeDrug, id, values) => {
    setIsLoading(true);
    setIsLoadingEnabledAct(true);
    const newCurrentOrder = {
      id,
      status: statusObj.nextStatus(isFreeDrug),
      orderDate: moment(values.orderDate).toISOString(),
      administrationDate,
      estimatedDelivery: values.estimatedDelivery ? moment(values.estimatedDelivery).toISOString() : null
    };
    editCurrentOrderAct(newCurrentOrder, displayOrder.pharmacyPatient);
  };

  const onClick = (statusObj, orderIndex, isFreeDrug, pharmacyPatient, id, currentAdministrationDate, arriveDate) => {
    setIsLoadingEnabledAct(true);
    if (statusObj.key === Keys.order_required) {
      setOrdedModalVisible(true);
    } else if (isLastCycle(statusObj, isFreeDrug, orderIndex)) {
      setNoCyclesModalVisible(true);
    } else {
      setIsLoading(true);
      setIsLoadingEnabledAct(true);
      if (isDone(statusObj, isFreeDrug)) {
        const isCurrentOrder = patient.pharmacyPatientCurrentOrder === id;

        if (isCurrentOrder) {
          setNextCycleAct(pharmacyPatient, currentAdministrationDate, orderIndex);
        } else {
          // Non-current order: Just navigate to next order
          const cycles = patientCycles || [];
          const nextOrder = cycles.find((cycle) => cycle.orderIndex === orderIndex + 1);

          if (nextOrder) {
            dispatch(ACTIONS.setSelectedOrder(nextOrder.id));
          }

          setIsLoading(false);
          setIsLoadingEnabledAct(false);
        }
      } else {
        const newCurrentOrder = {
          id,
          status: statusObj.nextStatus(isFreeDrug),
          administrationDate: currentAdministrationDate,
          // Note: We need to populate the arriveDate as today date, when the user mark the order as arrived unless it was set before.
          arriveDate: !arriveDate && statusObj.key === Keys.in_transit ? moment().toISOString() : arriveDate
        };
        editCurrentOrderAct(newCurrentOrder, displayOrder.pharmacyPatient);
      }
    }
  };

  const { numberOfCycles, isActive } = patient;
  const {
    status,
    orderIndex,
    isFreeDrug,
    id,
    pharmacyPatient,
    orderDate,
    estimatedDelivery,
    arriveDate,
    administrationDate
  } = displayOrder;

  const statusObj = Object.values(Statuses).find((s) => s.key === status);

  return (
    <>
      <ButtonContainer isCalenderView={isCalenderView} size={4} id={`${actionButtonId}_container`}>
        {isOverdue(orderDate, status, isFreeDrug) && isActive && showOverdue ? (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: 14
              }}
            >
              <Tooltip title={<Text>{t("overdue")}</Text>} color={"white"}>
                <IsOverdueIcon />
              </Tooltip>
            </div>
          </>
        ) : null}

        {statusObj && (
          <Button
            id={actionButtonId}
            loading={isLoading}
            type="primary"
            style={{ width: "155px" }}
            ghost={isActive && !isDone(statusObj, isFreeDrug)}
            onClick={() =>
              onClick(statusObj, orderIndex, isFreeDrug, pharmacyPatient, id, administrationDate, arriveDate)
            }
            disabled={!isActive || isLastStatusAllOrdesrView(statusObj, isFreeDrug, patient)}
          >
            {isActive ? statusObj.action(t, isFreeDrug) : "Inactive"}
          </Button>
        )}
      </ButtonContainer>
      <Modal
        centered={true}
        open={ordedModalVisible}
        onCancel={() => !isLoading && setOrdedModalVisible(false)}
        footer={null}
        title={
          <div style={{ width: "95%" }}>
            <Text>{t("action_modal.ordered.title", { site, id: patient.patient.id })}</Text>
          </div>
        }
        width={300}
      >
        <Form
          form={datesForm}
          initialValues={{
            orderDate: moment(orderDate),
            estimatedDelivery: estimatedDelivery ? moment(estimatedDelivery) : estimatedDelivery
          }}
          onFinish={(values) => onClickDrugOrdered(statusObj, isFreeDrug, id, values)}
        >
          <StyledDiv style={{ display: "flex", flexDirection: "column" }}>
            <OrderDateFormItem
              title={t("action_modal.ordered.order_date_label")}
              showLabel={true}
              strong={true}
              style={{ display: "inline-block" }}
            />
            <EstimatedDateFormItem showLabel={true} strong={true} style={{ display: "inline-block" }} />
          </StyledDiv>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              {t("submit")}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        centered={true}
        open={noCyclesModalVisible}
        onCancel={() => !isLoading && setNoCyclesModalVisible(false)}
        footer={null}
        title={
          <div style={{ width: "95%" }}>
            <Text>{t("action_modal.no_cycles.title", { site, id: patient.patient.id })}</Text>
          </div>
        }
        width={300}
        bodyStyle={{
          display: "flex",
          justifyContent: "center",
          alignContent: "center"
        }}
      >
        <Space direction={"vertical"}>
          <Title level={5}>{t("action_modal.no_cycles.select_one")}</Title>
          <Button
            type="primary"
            style={{ width: "100%" }}
            onClick={() => onClickAddCycle()}
            disabled={isLoading}
            loading={isLoading}
          >
            {t("action_modal.no_cycles.add_cycle")}
          </Button>
          <Button style={{ width: "100%" }} onClick={() => onClickSetInactive()}>
            {t("action_modal.no_cycles.set_inactive")}
          </Button>
        </Space>
      </Modal>
    </>
  );
};

const mapStateToProps = createStructuredSelector({
  isFetching: selectIsFetching([
    ApiNames.EditPatientCycle,
    ApiNames.NextCycle,
    ApiNames.PharmacyPatients,
    ApiNames.PatientPhiInfo
  ]),
  isSearchWithoutRefreshLoading: selectIsFetching([ApiNames.SearchWithoutRefresh]),
  site: selectSite
});

const mapDispatchToProps = (dispatch) => ({
  editCurrentOrderAct: (currentOrder, pharmacyPatient) =>
    dispatch(ACTIONS.editCurrentOrderAction(currentOrder, pharmacyPatient)),
  setNextCycleAct: (pharmacyPatient, currentAdministrationDate, currentOrderIndex) =>
    dispatch(ACTIONS.setNextCycleAction(pharmacyPatient, currentAdministrationDate, currentOrderIndex)),
  setPharmacyPatientAct: (pharmacyPatient) => dispatch(ACTIONS.setPharmacyPatientAction(pharmacyPatient)),
  addPatientCycleAct: (pharmacyPatient, setNextCycle) =>
    dispatch(ACTIONS.addPatientCycleAction(pharmacyPatient, setNextCycle))
});

const ActionButton = connect(mapStateToProps, mapDispatchToProps)(ActionButtonComponent);

export { ActionButton };
